import { useEffect, useRef, useState } from "react";

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function FinalCTA() {
  const { ref, inView } = useInView();

  const scrollToBooking = () => {
    const el = document.getElementById("booking");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative bg-[#060606] py-40 px-6 overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/a0248ab16da614f2aed16e2950d062ce.jpg"
          alt="Eastern Europe business expansion"
          className="w-full h-full object-cover object-top opacity-8"
        />
        <div className="absolute inset-0 bg-[#060606]/90" />
      </div>

      {/* Top / bottom lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A84C]/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A84C]/15 to-transparent" />

      <div ref={ref} className="max-w-4xl mx-auto relative text-center">
        <div className={`transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-16 h-px bg-[#C9A84C]/40" />
            <span className="text-[#C9A84C] text-xs tracking-[0.3em] uppercase font-light font-['Outfit']">Get Started</span>
            <div className="w-16 h-px bg-[#C9A84C]/40" />
          </div>

          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-7xl font-light text-white leading-tight mb-6">
            Ready to win your first clients<br />
            in <em className="text-[#C9A84C] not-italic">Eastern Europe?</em>
          </h2>

          <p className="text-white/45 text-lg font-light leading-relaxed font-['Outfit'] mb-14 max-w-xl mx-auto">
            Let&apos;s build your pipeline and close your first deals.
          </p>

          <div className={`flex flex-col sm:flex-row gap-5 items-center justify-center transition-all duration-700 delay-200 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <button
              onClick={scrollToBooking}
              className="group px-12 py-4 bg-[#C9A84C] text-[#080808] text-sm font-semibold tracking-[0.2em] uppercase hover:bg-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
            >
              Book a Strategy Call
              <span className="inline-block ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
            <a
              href="mailto:alex@emcalu.com"
              rel="nofollow"
              className="group px-12 py-4 border border-white/20 text-white/60 text-sm font-light tracking-[0.2em] uppercase hover:border-[#C9A84C] hover:text-[#C9A84C] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Email Us
            </a>
          </div>

          {/* Trust signals */}
          <div className={`mt-16 flex flex-wrap items-center justify-center gap-8 transition-all duration-700 delay-400 ${inView ? "opacity-100" : "opacity-0"}`}>
            {["Romania", "Moldova", "France", "Germany", "UK", "Benelux"].map((market) => (
              <span key={market} className="text-white/25 text-xs tracking-[0.2em] uppercase font-light font-['Outfit']">
                {market}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
